import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Switch } from './ui/switch';
import { Calculator, AlertTriangle, Loader2, Shield, FileText, Target, Settings, Leaf } from 'lucide-react';
import { TERRITOIRES } from './SimulateurOscopConstants';

export default function SimulateurOptionsStep({
  territoire, secteur, getSecteurIcon, montantProjet, dureeMois,
  adhesionOscop, setAdhesionOscop, adhesionFtpe, setAdhesionFtpe,
  activationPrevention, setActivationPrevention,
  error, prevStep, runSimulation, loading,
}) {
  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-600 mb-3">
          <Settings className="h-6 w-6 text-white" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Options ESS</h3>
        <p className="text-gray-500">Configurez vos options solidaires pour optimiser votre simulation</p>
      </div>

      {/* Récapitulatif */}
      <Card className="bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200">
        <CardContent className="p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Récapitulatif
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Territoire</span>
              <p className="font-medium">{TERRITOIRES[territoire]?.icon} {territoire}</p>
            </div>
            <div>
              <span className="text-gray-500">Secteur</span>
              <p className="font-medium flex items-center gap-1">
                {getSecteurIcon(secteur)} {secteur}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Montant</span>
              <p className="font-medium">{parseFloat(montantProjet || 0).toLocaleString('fr-FR')} €</p>
            </div>
            <div>
              <span className="text-gray-500">Durée</span>
              <p className="font-medium">{dureeMois/12} ans</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Options */}
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 rounded-xl border-2 border-green-200 bg-green-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500 rounded-lg">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-green-900">Adhésion O'SCOP</p>
              <p className="text-sm text-green-700">Bonus de réduction : 1%</p>
            </div>
          </div>
          <Switch
            checked={adhesionOscop}
            onCheckedChange={setAdhesionOscop}
          />
        </div>

        <div className="flex items-center justify-between p-4 rounded-xl border-2 border-purple-200 bg-purple-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500 rounded-lg">
              <Leaf className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-purple-900">Adhésion FTPE</p>
              <p className="text-sm text-purple-700">Bonus de réduction : 0.5%</p>
            </div>
          </div>
          <Switch
            checked={adhesionFtpe}
            onCheckedChange={setAdhesionFtpe}
          />
        </div>

        <div className="flex items-center justify-between p-4 rounded-xl border-2 border-blue-200 bg-blue-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <Target className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-blue-900">Prévention active</p>
              <p className="text-sm text-blue-700">Bonus de réduction : 0.3%</p>
            </div>
          </div>
          <Switch
            checked={activationPrevention}
            onCheckedChange={setActivationPrevention}
          />
        </div>
      </div>

      {/* Erreur - Gestion des restrictions HARD */}
      {error && (
        error.type === 'blocked' ? (
          <Alert className="border-red-300 bg-red-50">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <AlertTitle className="text-red-800 font-bold flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Simulation bloquée - {error.restriction_name || error.restriction_id}
            </AlertTitle>
            <AlertDescription className="text-red-700 mt-2">
              <p className="font-medium">{error.message?.fr || error.message}</p>
              {error.legal_notice && (
                <p className="text-sm mt-2 italic text-red-600">
                  {error.legal_notice?.fr || error.legal_notice}
                </p>
              )}
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Erreur</AlertTitle>
            <AlertDescription>{typeof error === 'string' ? error : JSON.stringify(error)}</AlertDescription>
          </Alert>
        )
      )}

      <div className="flex gap-3">
        <Button variant="outline" onClick={prevStep} className="flex-1">
          Retour
        </Button>
        <Button
          onClick={runSimulation}
          disabled={loading}
          className="flex-1 h-12 text-lg bg-gradient-to-r from-green-600 to-emerald-600"
        >
          {loading ? (
            <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Calcul en cours...</>
          ) : (
            <><Calculator className="mr-2 h-5 w-5" /> Lancer la simulation</>
          )}
        </Button>
      </div>
    </div>
  );
}
